import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ApiUrl from '../../utils/config';
import axios from 'axios';
import { Gender, RegisterModel, RoleClient } from '../../utils/apiClient';
import { DateLocal, DateType } from '../../common/components/DateLocal/DateLocal';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../../utils/passwordPolicy';
import { parseApiError } from '../../utils/apiError';
import {
  confirmRegistration,
  resendRegistrationCode,
  REGISTRATION_CODE_LENGTH,
  REGISTRATION_RESEND_SECONDS,
} from '../../utils/registrationApi';
import './Register.scss';

const EyeIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M4 4l16 16" />}
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const MINOR_AGE = 18;

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const calcAge = (birth: Date, now: Date = new Date()): number => {
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthday = now.getMonth() < birth.getMonth()
    || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: Gender.Male, label: 'Мужской' },
  { value: Gender.Female, label: 'Женский' },
];

const Register: React.FC = () => {
  const [formData, setFormData] = useState<RegisterModel>(new RegisterModel({
    lastName: '',
    firstName: '',
    patronymic: '',
    birthDate: new Date(),
    city: '',
    email: '',
    password: '',
    roleIds: [] as number[]
  }));
  const [consent, setConsent] = useState(false);
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  // Шаг 1 — анкета, шаг 2 — ввод кода из письма
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeInfo, setCodeInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { register } = useAuth();
  const roleClient = new RoleClient(ApiUrl, axios);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await roleClient.getRoles();
        // При самостоятельной регистрации доступны только эти роли (сервер тоже отбрасывает остальные).
        // Судья, администратор и т. д. назначаются только администратором.
        setRoles(response.filter(role => role.name === 'Спортсмен' || role.name === 'Тренер'));
      } catch (error) {
        console.error('Error fetching roles:', error);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') setEmailError('');
    setFormData((prevState) => new RegisterModel({
      ...prevState,
      [name]: value
    }));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roleId = parseInt(e.target.value);
    const isChecked = e.target.checked;

    setFormData((prevState) => new RegisterModel({
      ...prevState,
      roleIds: isChecked
        ? [...(prevState.roleIds ?? []), roleId]
        : (prevState.roleIds ?? []).filter(id => id !== roleId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setConsentError('');

    if (birthDateUnset) {
      setError('Укажите дату рождения.');
      return;
    }

    if (!formData.gender) {
      setError('Укажите пол: он нужен для распределения по категориям на турнирах.');
      return;
    }

    // Validate password
    if (formData.password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    const passwordError = validatePassword(formData.password, formData.email);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!consent) {
      setConsentError('Для регистрации необходимо согласие на обработку персональных данных.');
      return;
    }
    if (isMinor && !guardianConsent) {
      setConsentError('Для участников младше 18 лет нужно подтвердить согласие родителя или законного представителя.');
      return;
    }

    setSubmitting(true);
    try {
      // Сервер не создаёт аккаунт сразу: он отправляет на почту код, а аккаунт появится после подтверждения
      await register(new RegisterModel({
        ...formData,
        email: formData.email.trim(),
        personalDataConsent: consent,
        guardianConsent: isMinor ? guardianConsent : false,
      }));
      setCode('');
      setCodeError('');
      setCodeInfo('');
      setResendIn(REGISTRATION_RESEND_SECONDS);
      setStep('code');
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.field === 'email' && apiError.message) {
        // Например, такая почта уже есть в базе — показываем ошибку прямо у поля
        setEmailError(apiError.message);
        emailInputRef.current?.focus();
      } else if (apiError.field === 'consent' && apiError.message) {
        setConsentError(apiError.message);
      } else {
        setError(apiError.message || 'Ошибка регистрации. Попробуйте еще раз.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Обратный отсчёт до возможности запросить код повторно
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  // Вернуться к анкете (например, чтобы исправить адрес): введённые данные сохраняются
  const backToForm = (message = '') => {
    setStep('form');
    setError(message);
    setCode('');
    setCodeError('');
    setCodeInfo('');
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifying) return;
    if (code.length !== REGISTRATION_CODE_LENGTH) {
      setCodeError(`Введите ${REGISTRATION_CODE_LENGTH}-значный код из письма`);
      return;
    }

    setCodeError('');
    setCodeInfo('');
    setVerifying(true);
    try {
      await confirmRegistration(formData.email.trim(), code);
      navigate('/login', { state: { registered: true } });
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.field === 'email' && apiError.message) {
        backToForm();
        setEmailError(apiError.message);
      } else if (apiError.expired) {
        backToForm(apiError.message || 'Заявка устарела. Заполните форму заново.');
      } else {
        setCodeError(apiError.message || 'Не удалось подтвердить код. Попробуйте ещё раз.');
        setCode('');
        codeInputRef.current?.focus();
      }
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendIn > 0) return;
    setCodeError('');
    setCodeInfo('');
    setResending(true);
    try {
      await resendRegistrationCode(formData.email.trim());
      setCode('');
      setCodeInfo('Новый код отправлен. Предыдущий больше не действует.');
      setResendIn(REGISTRATION_RESEND_SECONDS);
      codeInputRef.current?.focus();
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.expired) {
        backToForm(apiError.message || 'Заявка устарела. Заполните форму заново.');
      } else {
        setCodeError(apiError.message || 'Не удалось отправить код. Попробуйте позже.');
      }
    } finally {
      setResending(false);
    }
  };

  const birthDate = formData.birthDate ? new Date(formData.birthDate) : new Date();
  // Пока дата рождения равна сегодняшней, считаем, что пользователь её ещё не указал
  const birthDateUnset = isSameDay(birthDate, new Date());
  const isMinor = !birthDateUnset && calcAge(birthDate) < MINOR_AGE;
  const passwordMismatch = confirmPassword.length > 0 && confirmPassword !== formData.password;
  const passwordType = showPassword ? 'text' : 'password';

  return (
    <div className="register">
      <header className="register__header">
        <h2 className="register__title">Регистрация</h2>
        <p className="register__subtitle">
          {step === 'form'
            ? 'Создайте аккаунт, чтобы участвовать в турнирах JUDO40'
            : 'Остался один шаг — подтвердите адрес электронной почты'}
        </p>
      </header>

      {step === 'form' && (
      <form className="register__form" onSubmit={handleSubmit}>
        {error && <div className="register__alert" role="alert">{error}</div>}

        {/* Строка 1: ФИО */}
        <div className="register__row">
          <div className="register__field">
            <label className="register__label" htmlFor="reg-lastName">Фамилия</label>
            <input
              id="reg-lastName"
              className="register__input"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              autoComplete="family-name"
              placeholder="Иванов"
              required
            />
          </div>
          <div className="register__field">
            <label className="register__label" htmlFor="reg-firstName">Имя</label>
            <input
              id="reg-firstName"
              className="register__input"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              autoComplete="given-name"
              placeholder="Иван"
              required
            />
          </div>
          <div className="register__field">
            <label className="register__label" htmlFor="reg-patronymic">
              Отчество <span className="register__optional">необязательно</span>
            </label>
            <input
              id="reg-patronymic"
              className="register__input"
              type="text"
              name="patronymic"
              value={formData.patronymic}
              onChange={handleInputChange}
              autoComplete="additional-name"
              placeholder="Иванович"
            />
          </div>
        </div>

        {/* Строка 2: дата рождения, город, email */}
        <div className="register__row">
          <div className="register__field">
            <span className="register__label">Дата рождения</span>
            <div className="register__date">
              <DateLocal
                onChange={(date) => date && setFormData((prevState) => new RegisterModel({
                  ...prevState,
                  birthDate: date,
                }))}
                type={DateType.date}
                value={formData.birthDate ?? new Date()}
                max={new Date()}
                width={200}
                isClearable={false}
              />
            </div>
          </div>
          <div className="register__field">
            <label className="register__label" htmlFor="reg-city">Город</label>
            <input
              id="reg-city"
              className="register__input"
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              autoComplete="address-level2"
              placeholder="Москва"
              required
            />
          </div>
          <div className="register__field">
            <label className="register__label" htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              ref={emailInputRef}
              className={`register__input${emailError ? ' register__input--invalid' : ''}`}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              placeholder="name@example.com"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'reg-email-error' : undefined}
              required
            />
            {emailError ? (
              <span id="reg-email-error" className="register__hint register__hint--error" role="alert">
                {emailError}{' '}
                <Link to="/login" className="register__hint-link">Войти</Link>
                {' · '}
                <Link to="/forgot-password" className="register__hint-link">Забыли пароль?</Link>
              </span>
            ) : (
              <span className="register__hint">На неё придёт код подтверждения</span>
            )}
          </div>
        </div>

        {/* Строка 3: пароль и подтверждение */}
        <div className="register__row register__row--two">
          <div className="register__field">
            <label className="register__label" htmlFor="reg-password">Пароль</label>
            <div className="register__control">
              <input
                id="reg-password"
                className="register__input register__input--with-action"
                type={passwordType}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="register__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showPassword}
                title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>
          <div className="register__field">
            <label className="register__label" htmlFor="reg-confirm">Подтвердите пароль</label>
            <input
              id="reg-confirm"
              className={`register__input${passwordMismatch ? ' register__input--invalid' : ''}`}
              type={passwordType}
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              aria-invalid={passwordMismatch}
              required
            />
            {passwordMismatch && <span className="register__hint register__hint--error">Пароли не совпадают</span>}
          </div>
        </div>

        {/* Строка 4: пол */}
        <div className="register__row register__row--single">
          <div className="register__field">
            <span className="register__label" id="reg-gender-label">Пол</span>
            <div className="register__roles register__roles--compact" role="radiogroup" aria-labelledby="reg-gender-label">
              {GENDER_OPTIONS.map((option) => {
                const checked = formData.gender === option.value;
                return (
                  <label key={option.value} className={`register__role${checked ? ' register__role--active' : ''}`}>
                    <input
                      className="register__role-input"
                      type="radio"
                      name="gender"
                      value={option.value}
                      checked={checked}
                      onChange={() => setFormData((prevState) => new RegisterModel({ ...prevState, gender: option.value }))}
                    />
                    <span className="register__role-mark"><CheckIcon /></span>
                    <span className="register__role-name">{option.label}</span>
                  </label>
                );
              })}
            </div>
            <span className="register__hint">Нужен для подбора категории на соревнованиях</span>
          </div>
        </div>

        {/* Строка 5: выбор роли на всю ширину */}
        <div className="register__row register__row--single">
          <div className="register__field">
            <span className="register__label" id="reg-roles-label">Роль</span>
            <div className="register__roles" role="group" aria-labelledby="reg-roles-label">
              {rolesLoading && (
                <>
                  <span className="register__role register__role--skeleton" />
                  <span className="register__role register__role--skeleton" />
                </>
              )}
              {!rolesLoading && roles.length === 0 && (
                <span className="register__hint">Список ролей недоступен</span>
              )}
              {roles.map((role) => {
                const checked = formData.roleIds?.includes(role.id) ?? false;
                return (
                  <label key={role.id} className={`register__role${checked ? ' register__role--active' : ''}`}>
                    <input
                      className="register__role-input"
                      type="checkbox"
                      value={role.id}
                      checked={checked}
                      onChange={handleRoleChange}
                    />
                    <span className="register__role-mark"><CheckIcon /></span>
                    <span className="register__role-name">{role.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <PasswordRequirements password={formData.password} />

        <div className="register__consents">
          <label className={`register__check${consentError && !consent ? ' register__check--invalid' : ''}`}>
            <input
              className="register__check-input"
              type="checkbox"
              checked={consent}
              onChange={(e) => { setConsent(e.target.checked); setConsentError(''); }}
            />
            <span className="register__check-box"><CheckIcon /></span>
            <span>
              Я даю согласие на обработку моих персональных данных на условиях{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Политики конфиденциальности</Link>{' '}
              и принимаю <Link to="/terms" target="_blank" rel="noopener noreferrer">Пользовательское соглашение</Link>
            </span>
          </label>
          {isMinor && (
            <label className={`register__check${consentError && !guardianConsent ? ' register__check--invalid' : ''}`}>
              <input
                className="register__check-input"
                type="checkbox"
                checked={guardianConsent}
                onChange={(e) => { setGuardianConsent(e.target.checked); setConsentError(''); }}
              />
              <span className="register__check-box"><CheckIcon /></span>
              <span>
                Мне ещё нет 18 лет. Подтверждаю, что родитель или законный представитель согласен
                на регистрацию и обработку моих персональных данных
              </span>
            </label>
          )}
          {consentError && <span className="register__hint register__hint--error" role="alert">{consentError}</span>}
        </div>

        <div className="register__actions">
          <Link to="/login" className="register__login-link">
            Уже есть аккаунт? <span>Войти</span>
          </Link>
          <button type="submit" className="btn register__submit" disabled={submitting}>
            {submitting ? 'Отправляем код…' : 'Продолжить'}
          </button>
        </div>
      </form>
      )}

      {step === 'code' && (
        <form className="register__form register__verify" onSubmit={handleConfirm}>
          <div className="register__verify-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <path d="M3.5 7l8.5 6 8.5-6" />
            </svg>
          </div>
          <h3 className="register__verify-title">Введите код из письма</h3>
          <p className="register__verify-text">
            Мы отправили {REGISTRATION_CODE_LENGTH}-значный код на<br />
            <strong>{formData.email.trim()}</strong>
          </p>

          {codeError && <div className="register__alert" role="alert">{codeError}</div>}
          {codeInfo && !codeError && <div className="register__notice" role="status">{codeInfo}</div>}

          <input
            ref={codeInputRef}
            className={`register__input register__code${codeError ? ' register__input--invalid' : ''}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={REGISTRATION_CODE_LENGTH}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, REGISTRATION_CODE_LENGTH));
              setCodeError('');
            }}
            placeholder="······"
            aria-label="Код подтверждения"
          />

          <button
            type="submit"
            className="btn register__submit register__verify-submit"
            disabled={verifying || code.length !== REGISTRATION_CODE_LENGTH}
          >
            {verifying ? 'Проверяем…' : 'Подтвердить и завершить'}
          </button>

          <div className="register__verify-links">
            <button
              type="button"
              className="register__link-button"
              onClick={handleResend}
              disabled={resending || resendIn > 0}
            >
              {resendIn > 0 ? `Отправить код ещё раз через ${resendIn} с` : 'Отправить код ещё раз'}
            </button>
            <button type="button" className="register__link-button" onClick={() => backToForm()}>
              Изменить email или данные
            </button>
          </div>
          <p className="register__hint register__verify-spam">Письма нет? Проверьте папку «Спам».</p>
        </form>
      )}
    </div>
  );
};

export default Register;
